import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { api, errMsg, REGIMI } from "@/lib/api";
import { Field } from "@/components/Common";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NONE = "__none";
const COMPANY_FIELDS = [["codice_fiscale", "Codice fiscale"], ["indirizzo", "Indirizzo", "sm:col-span-2"], ["cap", "CAP"],
  ["comune", "Comune"], ["provincia", "Provincia"], ["pec", "PEC"]];

const Pick = ({ label, value, onChange, items, labelKey, testid }) => (
  <Field label={label}>
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>Nessuno</SelectItem>
        {items.map((s) => <SelectItem key={s.id} value={s.id}>{s[labelKey]}</SelectItem>)}
      </SelectContent>
    </Select>
  </Field>
);

export const ActivateDialog = ({ req, onClose, onDone }) => {
  const [f, setF] = useState(null);
  const [studios, setStudios] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const isCompany = req?.tipo === "azienda";

  useEffect(() => {
    if (!req) return;
    setF({ denominazione: req.denominazione, piva: req.piva || "", codice_fiscale: req.codice_fiscale || "", regime_fiscale: req.regime_fiscale || "RF01",
      indirizzo: req.indirizzo || "", cap: req.cap || "", comune: req.comune || "", provincia: req.provincia || "", email: req.email,
      pec: req.pec || "", telefono: req.telefono || "", studio_id: null, package_id: req.package_id || null,
      admin_name: req.nome, admin_email: req.email });
    if (req.tipo === "azienda") {
      api.get("/admin/studios").then((r) => setStudios(r.data));
      api.get("/admin/packages").then((r) => setPkgs(r.data));
    }
  }, [req]);

  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/requests/${req.id}/activate`, f);
      if (data.email_sent) toast.success(`Account creato: email di benvenuto inviata a ${f.admin_email}`);
      else toast.warning("Account creato, ma l'email di benvenuto non è partita. Reinviala dalla sezione Utenti");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Crea {isCompany ? "azienda" : "studio"} dalla richiesta</DialogTitle>
          <DialogDescription>Il referente riceverà un'email di benvenuto con il link per scegliere la propria password.</DialogDescription>
        </DialogHeader>
        {f && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={isCompany ? "Ragione sociale" : "Nome studio"} className="sm:col-span-2">
              <Input data-testid="activate-denominazione-input" value={f.denominazione} onChange={(e) => set("denominazione", e.target.value)} />
            </Field>
            <Field label="Partita IVA"><Input data-testid="activate-piva-input" value={f.piva} onChange={(e) => set("piva", e.target.value)} /></Field>
            {isCompany ? (
              <>
                {COMPANY_FIELDS.map(([k, l, c]) => (
                  <Field key={k} label={l} className={c}><Input data-testid={`activate-${k}-input`} value={f[k]} onChange={(e) => set(k, e.target.value)} /></Field>
                ))}
                <Field label="Regime fiscale">
                  <Select value={f.regime_fiscale} onValueChange={(v) => set("regime_fiscale", v)}>
                    <SelectTrigger data-testid="activate-regime-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(REGIMI).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Pick label="Studio abbinato" testid="activate-studio-select" value={f.studio_id} items={studios} labelKey="denominazione" onChange={(v) => set("studio_id", v)} />
                <Pick label="Pacchetto XML" testid="activate-package-select" value={f.package_id} items={pkgs} labelKey="name" onChange={(v) => set("package_id", v)} />
              </>
            ) : (
              <Field label="Comune"><Input data-testid="activate-comune-input" value={f.comune} onChange={(e) => set("comune", e.target.value)} /></Field>
            )}
            <div className="grid gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:col-span-2 sm:grid-cols-2">
              <div className="text-sm font-semibold sm:col-span-2">Primo amministratore</div>
              <Field label="Nome"><Input data-testid="activate-admin-name-input" value={f.admin_name} onChange={(e) => set("admin_name", e.target.value)} /></Field>
              <Field label="Email (riceve il benvenuto)"><Input data-testid="activate-admin-email-input" type="email" value={f.admin_email} onChange={(e) => set("admin_email", e.target.value)} /></Field>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button data-testid="activate-submit-button" disabled={busy || !f?.denominazione || !f?.piva || !f?.admin_email} onClick={submit} className="bg-sky-600 hover:bg-sky-700">
            <UserPlus className="mr-2 h-4 w-4" />{busy ? "Creazione…" : "Crea account e invia benvenuto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
